/**
 * The one share reconcile (TMMAPP-0158; design §5.3, MM-D-115): `POST /v1/me/shares/reconcile` binds
 * the caller's accepted invitations, starting a guest seat for someone with no entitlement, then `/me`
 * is re-read so the new tier and virtual accounts take over. `SharedLanding`, the subscribe gate's
 * once-per-session check and the blocking dialog's "Check again" all go through here.
 *
 * It never throws: a 429 is `rateLimited` and any other failure is `failed`. An
 * `upstream: "unavailable"` answer is a normal answer.
 */
import { isApiError } from '@/api/errors';
import type { Me } from '@/api/me';
import { sharesApi } from '@/api/shares';

export interface ReconcileAnswer {
  reconciled: number;
  upstreamUnavailable: boolean;
}

export type ReconcileOutcome =
  | (ReconcileAnswer & {
      kind: 'answered';
      /** The re-read `/me`; `'notRead'` when `shouldRefetch` said no, `'readFailed'` when it failed. */
      me: Me | 'notRead' | 'readFailed';
    })
  | { kind: 'rateLimited' }
  | { kind: 'failed' };

export async function reconcileShares(
  refetch: () => Promise<Me | void>,
  shouldRefetch: (answer: ReconcileAnswer) => boolean,
): Promise<ReconcileOutcome> {
  let answer: ReconcileAnswer;
  try {
    const result = await sharesApi.reconcile();
    answer = { reconciled: result.reconciled, upstreamUnavailable: result.upstreamUnavailable };
  } catch (error) {
    return isApiError(error) && error.status === 429 ? { kind: 'rateLimited' } : { kind: 'failed' };
  }
  if (!shouldRefetch(answer)) return { kind: 'answered', ...answer, me: 'notRead' };
  const me = await refetch();
  return { kind: 'answered', ...answer, me: me ?? 'readFailed' };
}
